import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { CognitoUserPool, CognitoUser, CognitoUserSession, AuthenticationDetails } from 'amazon-cognito-identity-js';
import { environment } from '../../environments/environment';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private pool = new CognitoUserPool({
    UserPoolId: environment.cognito.userPoolId,
    ClientId: environment.cognito.clientId,
  });

  private session: CognitoUserSession | null = null;
  private pendingUser: CognitoUser | null = null;

  constructor(private router: Router) {
    // Restore session from storage on page load (e.g. after refresh)
    const user = this.pool.getCurrentUser();
    if (user) {
      user.getSession((err: Error | null, session: CognitoUserSession | null) => {
        if (!err && session?.isValid()) {
          this.session = session;
        }
      });
    }
  }

  loginWithPin(siteCode: string, pin: string): Promise<any> {
    const username = `${siteCode}.system@hb.im`;
    const user = new CognitoUser({ Username: username, Pool: this.pool });
    const auth = new AuthenticationDetails({ Username: username, Password: pin });
    return new Promise((res, rej) =>
      user.authenticateUser(auth, {
        onSuccess: (session) => { this.session = session; res(session); },
        onFailure: rej,
      })
    );
  }

  initiateEmailLogin(email: string): Promise<any> {
    const user = new CognitoUser({ Username: email, Pool: this.pool });
    this.pendingUser = user;
    return new Promise((res, rej) =>
      user.initiateAuth(new AuthenticationDetails({ Username: email }), {
        onSuccess: (session) => { this.session = session; res(session); },
        onFailure: rej,
        customChallenge: res,
      })
    );
  }

  verifyEmailCode(_email: string, code: string): Promise<any> {
    const user = this.pendingUser;
    if (!user) { return Promise.reject(new Error('No active login session — please re-enter your email.')); }
    return new Promise((res, rej) => {
      user.sendCustomChallengeAnswer(code, {
        onSuccess: (session) => { this.session = session; res(session); },
        onFailure: rej,
      });
    });
  }

  getToken(): string | null {
    return this.session?.getIdToken().getJwtToken() ?? null;
  }

  getRole(): string | null {
    return this.session?.getIdToken().decodePayload()['custom:role'] ?? null;
  }

  getLocationNum(): number {
    const v = this.session?.getIdToken().decodePayload()['custom:location_num'];
    return parseInt(v ?? '0', 10);
  }

  /** Returns a display-friendly identity: site code for location users, email for all others. */
  getUserIdentity(): string {
    const email = this.session?.getIdToken().decodePayload()['email'] ?? '';
    if (typeof email === 'string' && email.endsWith('.system@hb.im')) {
      return email.replace('.system@hb.im', '');
    }
    return email;
  }

  isLoggedIn(): boolean { return !!this.session?.isValid(); }

  logout(): void {
    const role = this.getRole();
    this.pool.getCurrentUser()?.signOut();
    this.session = null;
    this.pendingUser = null;
    // Clear all Cognito tokens from localStorage to prevent stale user data
    const keysToRemove = Object.keys(localStorage).filter(k =>
      k.startsWith('CognitoIdentityServiceProvider')
    );
    keysToRemove.forEach(k => localStorage.removeItem(k));
    // Full reload to clear all cached component state (Ionic keeps pages alive)
    const emailRoles = ['hub_manager', 'collector', 'post_office'];
    window.location.href = emailRoles.includes(role ?? '') ? '/auth/email' : '/auth/pin';
  }
}
